import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog.tsx";
import { BrandDots } from "@/components/brand/brand-dots.tsx";
import { DelegateLockup } from "@/components/brand/delegate-mark.tsx";
import { api, ApiError, type AvatarSummary, type Me } from "./api.ts";
import { Marketplace } from "./Marketplace.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select.tsx";
import { primeAvatarAudio } from "./avatar-audio.ts";

/* --------------------------------- login ---------------------------------- */

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [portal,setPortal]=useState<string|null>(null);
  useEffect(()=>{void api<{accountPortalUrl:string|null}>("/api/auth/options").then(r=>setPortal(r.accountPortalUrl));},[]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    primeAvatarAudio();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/login", { body: { email, password } });
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="grid min-h-dvh place-items-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <DelegateLockup className="mb-2" />
          <CardTitle>Sign in to Delegate</CardTitle>
          <CardDescription>Talk to your employees and pick up their saved work.</CardDescription>
        </CardHeader>
        <CardContent>
          {portal ? <Button asChild className="w-full"><a href={portal}>Sign in to Delegate</a></Button> : <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="grid gap-2">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              type="email"
              autoComplete="username"
              placeholder="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            </div>
            <div className="grid gap-2">
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              placeholder="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            </div>
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Signing in..." : "Sign in"}
            </Button>
          </form>}
        </CardContent>
      </Card>
    </main>
  );
}

/* -------------------------------- avatars --------------------------------- */

export function AvatarBar({
  me,
  activeId,
  onSelect,
  onChanged,
  disabled,
}: {
  me: Me;
  activeId: string | null;
  onSelect: (id: string) => void;
  onChanged: () => void;
  disabled: boolean;
}) {
  const [portal,setPortal]=useState<string|null>(null);
  useEffect(()=>{void api<{accountPortalUrl:string|null}>("/api/auth/options").then(r=>setPortal(r.accountPortalUrl));},[]);
  const [creating, setCreating] = useState(false);
  const [hiring, setHiring] = useState(false);

  const [pendingRemoval, setPendingRemoval] = useState<AvatarSummary | null>(null);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (!pendingRemoval || removing || disabled) return;
    setRemoving(true);
    setError(null);
    try {
      await api(`/api/avatars/${pendingRemoval.id}`, { method: "DELETE" });
      setPendingRemoval(null);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete employee. Try again.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <header className="border-b border-border bg-background">
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
        <div className="flex flex-wrap items-center gap-4">
          <DelegateLockup />
          <span className="text-sm text-muted-foreground">{me.tenant.name}</span>
        </div>
        {portal && <Button variant="outline" onClick={()=>location.assign(portal)}>Switch workspace</Button>}
        <Button variant="ghost" size="sm" onClick={async () => {
          try {
            await api("/api/auth/logout", { body: {} });
            location.reload();
          } catch (err) {
            setError(err instanceof Error ? err.message : "Could not sign out. Try again.");
          }
        }}>Sign out</Button>
      </div>
      <div className="flex flex-wrap items-center gap-4 px-6 pb-4">
      <BrandDots size="sm" />

      <div className="flex flex-1 flex-wrap items-center gap-2">
        {me.avatars.map((a) => (
          <div
            key={a.id}
            className="flex items-center gap-1"
          >
            <Button
              variant={a.id === activeId ? "secondary" : "ghost"}
              size="sm"
              disabled={disabled}
              onClick={() => onSelect(a.id)}
              title={a.role}
              aria-pressed={a.id === activeId}
            >
              {a.name}
            </Button>
            {!a.mandatory && (
              <Button
                variant="ghost"
                size="icon-sm"
                  disabled={disabled}
                onClick={() => { setError(null); setPendingRemoval(a); }}
                aria-label={`Delete ${a.name}`}
              >
                <Trash2 />
              </Button>
            )}
          </div>
        ))}
        <Button variant="outline" size="sm" disabled={disabled} onClick={() => setHiring(true)}>
          <Plus /> Marketplace
        </Button>
        <Button variant="outline" size="sm" disabled={disabled} onClick={() => setCreating(true)}>
          <Plus /> Custom employee
        </Button>
      </div>

      </div>
      {error && !pendingRemoval && <div className="px-6 pb-4"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}
      <AlertDialog open={pendingRemoval !== null} onOpenChange={(open) => { if (!open && !removing) { setPendingRemoval(null); setError(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingRemoval?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes this employee's tasks, topics and conversations. Facts about you stay in your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" disabled={removing || disabled} onClick={(event) => { event.preventDefault(); void remove(); }}>
              {removing ? "Deleting..." : "Delete employee"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {hiring && (
        <Marketplace
          onClose={() => setHiring(false)}
          onHired={(id) => {
            setHiring(false);
            onChanged();
            onSelect(id);
          }}
        />
      )}
      {creating && (
        <NewAvatar
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onChanged();
            onSelect(id);
          }}
        />
      )}
    </header>
  );
}

export function NewAvatar({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [voices, setVoices] = useState<{ voices: string[]; recommendedVoices: string[] }>({
    voices: ["marin"],
    recommendedVoices: ["marin"],
  });
  useEffect(() => {
    // The server owns the list; hardcoding it here is how it drifted to four.
    api<{ voices: string[]; recommendedVoices: string[] }>("/api/options")
      .then(setVoices)
      .catch(() => {});
  }, []);

  const [form, setForm] = useState({
    name: "",
    role: "",
    responsibility: "",
    voice: "marin",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { avatar } = await api<{ avatar: AvatarSummary }>("/api/avatars", { body: form });
      onCreated(avatar.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not create avatar");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>New employee</DialogTitle>
            <DialogDescription>
              Starts with no memory of its own. Tasks and conversations are separate from your other
              employees; facts about you are shared across them.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="avatar-name">Name</Label>
            <Input
              id="avatar-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Scout"
              required
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="avatar-role">Role</Label>
            <Input
              id="avatar-role"
              value={form.role}
              onChange={(e) => set("role", e.target.value)}
              placeholder="research delegate"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="avatar-responsibility">What it is responsible for</Label>
            <Textarea
              id="avatar-responsibility"
              rows={3}
              value={form.responsibility}
              onChange={(e) => set("responsibility", e.target.value)}
              placeholder="Find and summarise source material, and keep a reading list."
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="avatar-voice">Voice</Label>
            <Select value={form.voice} onValueChange={(v) => set("voice", v)}>
              <SelectTrigger id="avatar-voice" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {voices.voices.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                    {voices.recommendedVoices.includes(v) ? "  (recommended)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
